export const AI_MEAL_MACRO_MARGIN = 0.15;

export const roundMacro = (value, decimals = 1) => {
  const n = Number(value) || 0;
  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
};

export const isWithinAiMealMacroMargin = (actual, target) => {
  const t = Number(target) || 0;
  const a = Number(actual) || 0;
  if (t <= 0) return a <= 0;
  return a >= t * (1 - AI_MEAL_MACRO_MARGIN) && a <= t * (1 + AI_MEAL_MACRO_MARGIN);
};

export const getAiMealMacroRange = (target) => {
  const t = Number(target) || 0;
  return {
    min: Math.round(t * (1 - AI_MEAL_MACRO_MARGIN)),
    max: Math.round(t * (1 + AI_MEAL_MACRO_MARGIN)),
  };
};

export const normalizeNutrition = (nutrition = {}) => ({
  calories: Math.round(nutrition.calories || 0),
  protein: roundMacro(nutrition.protein),
  carbs: roundMacro(nutrition.carbs),
  fat: roundMacro(nutrition.fat),
});

export const sumIngredientNutrition = (ingredients = []) =>
  normalizeNutrition(
    ingredients.reduce(
      (acc, ing) => ({
        calories: acc.calories + (ing.calories || 0),
        protein: acc.protein + (ing.protein || 0),
        carbs: acc.carbs + (ing.carbs || 0),
        fat: acc.fat + (ing.fat || 0),
      }),
      { calories: 0, protein: 0, carbs: 0, fat: 0 }
    )
  );

export const normalizeAiDish = (apiData, mealSlotName) => {
  const ingredients = (apiData.ingredients || []).map((ing) => ({
    ...ing,
    item: ing.item || ing.name || '',
    household_measure: ing.household_measure || ing.display_amount || '',
  }));
  const fromIngredients = sumIngredientNutrition(ingredients);
  const apiNutrition = apiData.nutrition || {
    calories: apiData.total_calories,
    protein: apiData.total_protein,
    carbs: apiData.total_carbs,
    fat: apiData.total_fat,
  };
  const nutrition = ingredients.length > 0
    ? fromIngredients
    : normalizeNutrition(apiNutrition);
  return {
    meal_name: apiData.meal_name || mealSlotName || '',
    meal_title: apiData.meal_title || apiData.meal_name || '',
    main_protein_source: apiData.main_protein_source || '',
    ingredients,
    nutrition,
  };
};

export const normalizeMealNameForSave = (mealName, englishTranslations, hebrewTranslations) => {
  if (!mealName) return mealName;
  if (mealName.startsWith('meal.')) {
    return englishTranslations[mealName] || mealName;
  }
  for (const [key, value] of Object.entries(englishTranslations)) {
    if (key.startsWith('meal.') && value === mealName) {
      return value;
    }
  }
  for (const [key, value] of Object.entries(hebrewTranslations)) {
    if (key.startsWith('meal.') && value === mealName) {
      return englishTranslations[key];
    }
  }
  return mealName;
};

/** Same dish shape as main/alternative in meal_plan JSON. */
export const formatDishForMealPlanDb = (dish, englishMealName) => ({
  meal_name: englishMealName,
  meal_title: dish.meal_title || dish.meal_name || dish.title || englishMealName,
  nutrition: normalizeNutrition(
    dish.nutrition || {
      calories: dish.calories || 0,
      protein: dish.protein || 0,
      carbs: dish.carbs || 0,
      fat: dish.fat || 0,
    }
  ),
  ingredients: dish.ingredients || [],
  main_protein_source: dish.main_protein_source || '',
});

export const countAlternativeDishes = (meal) =>
  (meal.alternative ? 1 : 0) + (meal.alternatives?.length || 0);

export const normalizeMealAlternativeSlots = (meal) => {
  const all = [
    ...(meal.alternative ? [meal.alternative] : []),
    ...(meal.alternatives || []).filter(Boolean),
  ];
  if (all.length === 0) return { ...meal, alternative: null, alternatives: [] };
  if (all.length === 1) return { ...meal, alternative: all[0], alternatives: [] };
  return { ...meal, alternative: all[0], alternatives: all.slice(1) };
};

export const getAltDishFromMeal = (meal, type, altIndex) => {
  if (type === 'alternatives' && altIndex != null) {
    return meal.alternatives?.[altIndex] || null;
  }
  if (type === 'alternative') {
    return meal.alternative || null;
  }
  return null;
};

export const writeAltDishToMeal = (meal, type, altIndex, dish) => {
  if (type === 'alternatives' && altIndex != null) {
    const alternatives = [...(meal.alternatives || [])];
    alternatives[altIndex] = dish;
    return { ...meal, alternatives };
  }
  if (type === 'alternative') {
    return { ...meal, alternative: dish };
  }
  return meal;
};

export const removeAltDishFromMeal = (meal, type, altIndex) => {
  if (type === 'alternatives' && altIndex != null) {
    const alternatives = [...(meal.alternatives || [])];
    alternatives.splice(altIndex, 1);
    return { ...meal, alternatives };
  }
  if (type === 'alternative') {
    return { ...meal, alternative: null };
  }
  return meal;
};
